/**
 * Copy dos dois braços do signup-ux-ab, num lugar só.
 *
 * Espalhar as strings pelas cinco telas tornaria impossível auditar o
 * experimento (e foi assim que os CTAs viraram cinco "Continuar" iguais).
 *
 * Regras que o copy do braço `guided` respeita:
 *
 *  - CTA em primeira pessoa, confirmando o dado — sem imperativo e sem
 *    "continuar" / "finalizar" / "preencha" / "informe".
 *  - Nenhuma promessa de tempo ou urgência ("em minutos", "agora",
 *    "imediato", "sem espera"). O atendimento tem estado de descanso e os
 *    Termos tratam os tempos como médias informativas, não prazo — prometer
 *    velocidade seria propaganda enganosa (CDC art. 37).
 *  - Nenhuma afirmação sobre o serviço que o produto não entregue. Não
 *    existe lista de médicos nem escolha de especialista: o atendimento é
 *    de um clínico geral, por fila. Os CTAs das telas de dado não falam
 *    "especialistas", "buscando médicos" nem prometem plantão.
 *  - O CTA da Home no braço `guided` é a exceção pedida pelo stakeholder
 *    (11/09/2026): "Checar médicos disponíveis". É o verbo da ação (ir
 *    ver se há plantão), não uma lista de profissionais.
 *  - Subtítulo só afirma o que já é verdade documentada no próprio funil
 *    (ex.: o e-mail recebe receitas e atestados — EmailForm.jsx).
 */

const CTA = Object.freeze({
  nome: {
    control: { idle: 'Continuar', busy: 'Continuando…' },
    guided: { idle: 'Este é o nome', busy: 'Guardando…' },
  },
  email: {
    control: { idle: 'Continuar', busy: 'Continuando…' },
    guided: { idle: 'Este é o meu e-mail', busy: 'Guardando…' },
  },
  emailConfirm: {
    control: { idle: 'Continuar', busy: 'Continuando…' },
    guided: { idle: 'Confere', busy: 'Conferindo…' },
  },
  emailConfirmLast: {
    control: { idle: 'Confirmar e continuar', busy: 'Continuando…' },
    guided: { idle: 'Confere', busy: 'Conferindo…' },
  },
  cpf: {
    control: { idle: 'Continuar', busy: 'Continuando…' },
    guided: { idle: 'Este é o meu CPF', busy: 'Guardando…' },
  },
  telefone: {
    control: { idle: 'Continuar', busy: 'Continuando…' },
    guided: { idle: 'Este é o meu telefone', busy: 'Guardando…' },
  },
  /**
   * Última tela do braço `guided`: o telefone é o último dado e o aceite dos
   * Termos acontece aqui, sem tela dedicada. O rótulo precisa deixar o aceite
   * explícito — é o que sustenta o clickwrap.
   */
  telefoneComTermos: {
    control: { idle: 'Continuar', busy: 'Continuando…' },
    guided: { idle: 'Aceito os termos e quero seguir', busy: 'Guardando…' },
  },
  cupom: {
    control: { idle: 'Continuar', busy: 'Continuando…' },
    guided: { idle: 'Quero esse desconto', busy: 'Guardando…' },
  },
  termos: {
    control: { idle: 'Aceitar Termos', busy: 'Aceitando…' },
    guided: { idle: 'Aceito os termos', busy: 'Aceitando…' },
  },
});

/**
 * Texto do botão primário de uma etapa.
 *
 * @param {string} step     chave em CTA (ex.: 'cpf')
 * @param {object} opts
 * @param {boolean} opts.guided      braço `guided`?
 * @param {boolean} opts.submitting  em envio?
 * @returns {string}
 */
export function signupCta(step, { guided = false, submitting = false } = {}) {
  const entry = CTA[step];
  if (!entry) return submitting ? 'Continuando…' : 'Continuar';
  const arm = guided ? entry.guided : entry.control;
  return submitting ? arm.busy : arm.idle;
}

/**
 * CTA da Home (hero + sticky). Controle mantém o texto atual; guided usa
 * o verbo que o stakeholder escolheu no lugar de "Iniciar consulta agora".
 */
export function homeSignupCta({ guided = false } = {}) {
  return guided ? 'Checar médicos disponíveis' : 'Iniciar consulta agora';
}

/** Saudação com o primeiro nome, para as telas depois do nome. */
export function signupGreeting(fullName) {
  const first = String(fullName || '').trim().split(/\s+/)[0] || '';
  if (!first) return '';
  return `Tudo certo, ${first}`;
}

export const TERMS_INLINE_NOTICE_PREFIX = 'Ao tocar no botão abaixo, você declara que leu e aceita os ';

/**
 * Frases que ciclam abaixo da barra no braço `guided`. Sem prazo e sem
 * inventar envio que ainda não aconteceu — "preparando / organizando"
 * descreve o cadastro em andamento.
 */
export const SIGNUP_LIVE_STATUS = Object.freeze([
  'Preparando seu cadastro…',
  'Organizando seus dados…',
  'Guardando o que você já informou…',
  'Deixando tudo pronto para a consulta…',
]);

const STEP_TITLES = Object.freeze({
  '/consulta': 'Qual é o nome completo do paciente?',
  '/email': 'Qual é o seu e-mail?',
  '/email/confirm': 'Confirme seu e-mail',
  '/cpf': 'Qual é o CPF do paciente?',
  '/telefone': 'Qual é o seu telefone?',
  '/cupom-primeira': 'Cupom liberado para você',
  '/termos': 'Termos de Uso e Privacidade',
});

/** Título da etapa no card do braço `guided`. */
export function signupStepTitle(pathname) {
  const p = String(pathname || '').replace(/\/+$/, '') || '/';
  return STEP_TITLES[p] || '';
}

export const SIGNUP_UX_COPY_FOR_TESTS = CTA;
